import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, MapPin } from 'lucide-react';
import { INTERNAL_LINKS } from '@/utils/seoConfig';

/**
 * Country Page Links Component
 * Provides internal linking to country-specific invoice generator pages
 * Used in homepage hero section and footer navigation
 */

// Homepage Hero Section Links
export function CountryPageHeroLinks({ className = '' }) {
  return (
    <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${className}`}>
      {INTERNAL_LINKS.homepage.map((link, index) => (
        <Link
          key={index}
          to={link.url}
          className="group p-6 bg-white rounded-lg border border-gray-200 hover:border-emerald-300 hover:shadow-md transition-all duration-200"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-emerald-50 rounded-lg group-hover:bg-emerald-100 transition-colors">
              <FileText className="h-5 w-5 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-gray-900 group-hover:text-emerald-700 transition-colors">
              {link.text}
            </h3>
          </div>
          <p className="text-sm text-gray-600 group-hover:text-gray-700 transition-colors">
            {link.description}
          </p>
          <div className="mt-4 flex items-center text-emerald-600 text-sm font-medium">
            <span>Jetzt erstellen</span>
            <svg className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </Link>
      ))}
    </div>
  );
}

// Compact Links for Sidebar or Small Sections
export function CountryPageCompactLinks({ className = '' }) {
  return (
    <div className={`space-y-2 ${className}`}>
      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <MapPin className="h-4 w-4 text-emerald-600" />
        Rechnung erstellen
      </h4>
      {INTERNAL_LINKS.homepage.map((link, index) => (
        <Link
          key={index}
          to={link.url}
          className="block p-3 rounded-lg border border-gray-100 hover:border-emerald-200 hover:bg-emerald-50 transition-all duration-200 group"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700 group-hover:text-emerald-700 transition-colors">
              {link.text}
            </span>
            <svg className="h-4 w-4 text-gray-400 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </Link>
      ))}
    </div>
  );
}

// Footer Navigation Links
export function CountryPageFooterLinks({ className = '' }) {
  return (
    <div className={className}>
      <h4 className="font-semibold text-gray-900 mb-4">
        {INTERNAL_LINKS.footer.section}
      </h4>
      <ul className="space-y-2">
        {INTERNAL_LINKS.footer.links.map((link, index) => (
          <li key={index}>
            <Link
              to={link.url}
              className="text-gray-600 hover:text-emerald-600 transition-colors duration-200 text-sm"
            >
              {link.text}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Breadcrumb Navigation for Country Pages
export function CountryPageBreadcrumb({ currentCountry, className = '' }) {
  const countryNames = {
    'deutschland': 'Deutschland',
    'oesterreich': 'Österreich', 
    'schweiz': 'Schweiz'
  };
  
  return (
    <nav className={`flex items-center space-x-2 text-sm text-gray-600 ${className}`}>
      <Link to="/" className="hover:text-emerald-600 transition-colors">
        Home
      </Link>
      <span>/</span>
      <span className="text-gray-900 font-medium">
        Rechnung erstellen {countryNames[currentCountry]}
      </span>
    </nav>
  );
}

export default CountryPageHeroLinks;
